import { Button } from '@calizahq/react-components-v2'
import styled from '@emotion/styled'
import React from 'react'

import { useAlertContext } from '@/context'
import { HomeQualityCheckMutations } from '@/graphql/mutations/homeQualityChecks.mutations'
import { useMobile } from '@/hooks'
import { useGraphqlMutation } from '@/hooks/useGraphql'
import { HomeTimeline } from '@/types/Homes'
import { formatDate, parseDate } from '@/util'

type Props = {
  homeData: HomeTimeline
  refetchDetails: () => void
  toggle: () => void
}

export const CreateHomeQualityCheckInspection = ({ toggle, homeData, refetchDetails }: Props) => {
  const { isTablet } = useMobile()
  const { setAlert } = useAlertContext()
  const { mutate, isPending: isMutationLoading } = useGraphqlMutation(
    HomeQualityCheckMutations.createHomeQualityCheck(),
    {
      onSuccess: () => {
        toggle()
        refetchDetails()
        setAlert('Home Quality Check Inspection created!', 'success')
      },
      onError: () => {
        setAlert('Failed to create inspection. Please try again.', 'error')
      },
    },
  )

  const canCreate = !!homeData.previousReservationId || !!homeData.upcomingReservationId

  const handleCreateHomeQualityCheckInspection = () => {
    mutate({
      homeQualityCheck: {
        precedingReservationId: homeData.previousReservationId,
        upcomingReservationId: homeData.upcomingReservationId,
      },
    })
  }

  return (
    <HomeQualityCheckInspectionReviewStyled className="flex w-full flex-col flex-wrap px-6">
      <p className="mb-4 text-p-sm md:mb-4">
        By creating this Home Quality Check Inspection, you intend to complete a walkthrough of the Home to ensure that
        it meets Landing Standards for our Members.
      </p>

      <div className="home-quality-check-inspection-review-line-item">
        <p>Home</p>
        <p className="font-bold">{homeData.searchDocument}</p>
      </div>

      <div className="home-quality-check-inspection-review-line-item">
        <p>Previous Move-Out Date</p>
        <p className="font-bold">
          {formatDate(parseDate(homeData.previousReservationMoveOutDate), 'MMMM d, yyyy') || 'Unknown'}
        </p>
      </div>

      <div className="home-quality-check-inspection-review-line-item">
        <p>Upcoming Move-In Date</p>
        <p className="font-bold">
          {homeData.upcomingMoveInDate
            ? formatDate(parseDate(homeData.upcomingMoveInDate), 'MMMM d, yyyy') || 'Unknown'
            : 'N/A'}
        </p>
      </div>

      {!canCreate && (
        <p className="mb-2 text-p-xs text-semantic-error">A reservation context is required to create an inspection.</p>
      )}
      <div className="flex flex-col-reverse justify-between mt-2 md:flex-row">
        <Button
          className="w-full bg-action-secondary md:flex md:w-auto"
          onClick={toggle}
          size={isTablet ? 'medium' : 'small'}
          variant="secondary"
        >
          Cancel
        </Button>
        <Button
          className="mb-2 w-full bg-text-semantic-success md:mb-0 md:w-auto"
          disabled={isMutationLoading || !canCreate}
          isLoading={isMutationLoading}
          onClick={handleCreateHomeQualityCheckInspection}
          size={isTablet ? 'medium' : 'small'}
        >
          Create
        </Button>
      </div>
    </HomeQualityCheckInspectionReviewStyled>
  )
}

export const HomeQualityCheckInspectionReviewStyled = styled.div`
  .home-quality-check-inspection-review-line-item {
    display: flex;
    height: 3.5rem;
    width: 100%;
    justify-content: space-between;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    overflow: hidden;
    border-bottom-width: 1px;
    border-color: #e7e6e4;
    margin-bottom: 0.25rem;
    p {
      &:first-of-type {
        line-height: 1.2;
        color: #787573;
        display: flex;
        align-items: center;
      }

      &:last-of-type {
        max-width: 50%;
        text-align: right;
        display: flex;
        align-items: center;
      }
    }
  }

  .font-bold {
    font-weight: 700;
  }
`
